/**
 * Firebase Configuration Validator
 *
 * Use this to test if Firebase is properly configured
 */

import { auth, firestore, isFirebaseEnabled } from '../services/firebaseConfig';
import { collection, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { signInAnonymously, signOut } from 'firebase/auth';

export interface ValidationResult {
  isValid: boolean;
  checks: {
    configLoaded: boolean;
    authInitialized: boolean;
    firestoreInitialized: boolean;
    authWorks?: boolean;
    firestoreWorks?: boolean;
  };
  errors: string[];
  warnings: string[];
}

/**
 * Validate Firebase configuration and connectivity
 */
export const validateFirebaseSetup = async (): Promise<ValidationResult> => {
  const result: ValidationResult = {
    isValid: true,
    checks: {
      configLoaded: false,
      authInitialized: false,
      firestoreInitialized: false,
    },
    errors: [],
    warnings: [],
  };

  console.log('🔍 Starting Firebase validation...\n');

  // Check 1: Configuration loaded
  console.log('1️⃣ Checking Firebase configuration...');
  if (isFirebaseEnabled()) {
    result.checks.configLoaded = true;
    console.log('   ✅ Firebase config loaded');
  } else {
    result.isValid = false;
    result.checks.configLoaded = false;
    result.errors.push('Firebase configuration missing or invalid');
    console.log('   ❌ Firebase config not found');
    return result; // Exit early if config is missing
  }

  // Check 2: Auth initialized
  console.log('\n2️⃣ Checking Firebase Auth...');
  if (auth !== null) {
    result.checks.authInitialized = true;
    console.log('   ✅ Firebase Auth initialized');
  } else {
    result.isValid = false;
    result.checks.authInitialized = false;
    result.errors.push('Firebase Auth not initialized');
    console.log('   ❌ Firebase Auth failed to initialize');
  }

  // Check 3: Firestore initialized
  console.log('\n3️⃣ Checking Firestore...');
  if (firestore !== null) {
    result.checks.firestoreInitialized = true;
    console.log('   ✅ Firestore initialized');
  } else {
    result.isValid = false;
    result.checks.firestoreInitialized = false;
    result.errors.push('Firestore not initialized');
    console.log('   ❌ Firestore failed to initialize');
  }

  // Check 4: Test Auth (optional - anonymous sign in)
  if (auth) {
    console.log('\n4️⃣ Testing Auth connectivity...');
    try {
      const userCredential = await signInAnonymously(auth);
      result.checks.authWorks = true;
      console.log('   ✅ Auth works - Anonymous sign in successful');
      console.log(`   User ID: ${userCredential.user.uid}`);

      // Clean up - sign out
      await signOut(auth);
      console.log('   ✅ Sign out successful');
    } catch (error: any) {
      result.warnings.push(`Auth connectivity test failed: ${error.message}`);
      result.checks.authWorks = false;
      console.log(`   ⚠️  Auth test failed: ${error.message}`);
    }
  }

  // Check 5: Test Firestore (optional - write/delete test doc)
  if (firestore && auth?.currentUser) {
    console.log('\n5️⃣ Testing Firestore connectivity...');
    try {
      const testCollection = collection(firestore, 'test');
      const testDoc = await addDoc(testCollection, {
        test: true,
        timestamp: new Date().toISOString(),
      });
      console.log('   ✅ Firestore write successful');
      console.log(`   Test doc ID: ${testDoc.id}`);

      // Clean up - delete test doc
      await deleteDoc(doc(firestore, 'test', testDoc.id));
      result.checks.firestoreWorks = true;
      console.log('   ✅ Firestore delete successful');
    } catch (error: any) {
      // This might fail due to security rules, which is expected
      result.warnings.push(`Firestore connectivity test failed: ${error.message}`);
      result.checks.firestoreWorks = false;
      console.log(`   ⚠️  Firestore test failed: ${error.message}`);
      console.log('   💡 This is normal if security rules are enabled');
    }
  }

  // Summary
  console.log('\n' + '='.repeat(50));
  console.log('📊 VALIDATION SUMMARY');
  console.log('='.repeat(50));
  console.log(`Overall Status: ${result.isValid ? '✅ VALID' : '❌ INVALID'}`);
  console.log(`\nChecks:`);
  console.log(`  Config Loaded: ${result.checks.configLoaded ? '✅' : '❌'}`);
  console.log(`  Auth Initialized: ${result.checks.authInitialized ? '✅' : '❌'}`);
  console.log(`  Firestore Initialized: ${result.checks.firestoreInitialized ? '✅' : '❌'}`);
  if (result.checks.authWorks !== undefined) {
    console.log(`  Auth Works: ${result.checks.authWorks ? '✅' : '⚠️'}`);
  }
  if (result.checks.firestoreWorks !== undefined) {
    console.log(`  Firestore Works: ${result.checks.firestoreWorks ? '✅' : '⚠️'}`);
  }

  if (result.errors.length > 0) {
    console.log(`\n❌ Errors (${result.errors.length}):`);
    result.errors.forEach((err, i) => console.log(`  ${i + 1}. ${err}`));
  }

  if (result.warnings.length > 0) {
    console.log(`\n⚠️  Warnings (${result.warnings.length}):`);
    result.warnings.forEach((warn, i) => console.log(`  ${i + 1}. ${warn}`));
  }

  console.log('='.repeat(50) + '\n');

  return result;
};

/**
 * Quick validation check (just checks if services are initialized)
 */
export const quickValidation = (): boolean => {
  const enabled = isFirebaseEnabled();
  const authReady = auth !== null;
  const firestoreReady = firestore !== null;

  console.log('🔍 Quick Firebase Check:');
  console.log(`   Enabled: ${enabled ? '✅' : '❌'}`);
  console.log(`   Auth: ${authReady ? '✅' : '❌'}`);
  console.log(`   Firestore: ${firestoreReady ? '✅' : '❌'}`);

  return enabled && authReady && firestoreReady;
};
